import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  FileInfo,
  FileUploadError,
  GALAXY_UPLOADER_SERVICE_TOKEN,
  GalaxyImageUploaderService,
} from '@vendasta/galaxy/uploader';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UploadImageModalService } from './upload-image-modal.service';

@Component({
  selector: 'user-profile-upload-image-modal',
  styleUrls: ['./upload-image-modal.component.scss'],
  templateUrl: './upload-image-modal.component.html',
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: UploadImageModalService }],
})
export class UploadImageModalComponent implements OnInit, OnChanges, OnDestroy {
  files: FileInfo[] = [];
  partnerId: string;
  imgURL: string;
  saveDisabled = true;
  currentFile: FileInfo;
  maxDimensions = { width: 2500, height: 2500 };
  maxFileSize = 5200000;
  acceptedFiles = '.png,.svg,.jpg,.jpeg/*';
  uploadPhotoHint: string;
  fileErrored$$: BehaviorSubject<FileUploadError> = new BehaviorSubject(null);
  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<UploadImageModalComponent>,
    private snackService: SnackbarService,
    @Inject(GALAXY_UPLOADER_SERVICE_TOKEN) private uploadService: GalaxyImageUploaderService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.uploadService.fileErrored$$.pipe(filter((error) => !!error)).subscribe({
        next: (error: FileUploadError) => this.onError(error),
      }),
    );

    this.subscriptions.push(
      this.uploadService.fileUploaded$$.pipe(filter((fileInfo) => !!fileInfo)).subscribe({
        next: (fileInfo: FileInfo) => this.onUpload(fileInfo),
      }),
    );

    this.uploadPhotoHint = this.translate.instant('PHOTO.HINT');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxDimensions && changes.maxDimensions.previousValue !== changes.maxDimensions.currentValue) {
      this.uploadService.setMaxDimensions(this.maxDimensions);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  onError(fileError: FileUploadError): void {
    this.saveDisabled = false;
    this.snackService.openErrorSnack(fileError.error.message);
  }

  onUpload(file: FileInfo): void {
    this.saveDisabled = false;
    this.dialogRef.close({ url: file.url });
  }

  editImage(info: FileInfo): void {
    this.saveDisabled = false;
    this.currentFile = info;
  }

  endEdit(blob?: Blob): void {
    this.uploadService.setCroppedImage(this.currentFile, blob);

    this.currentFile = undefined;
  }

  continue(): void {
    this.saveDisabled = true;
    this.uploadService.uploadQueuedFiles();
  }

  onFilesChanged(files: FileInfo[]): void {
    this.files = files;

    if (this.files[0]) {
      this.editImage(this.files[0]);
    }
  }

  closeUploadImageModal(success: boolean): void {
    this.dialogRef.close(success);
  }

  deleteImage(): void {
    this.uploadService.removeFile(this.files[0]);
  }
}
